import React, { useEffect, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import { useFormik } from "formik";
import * as Yup from "yup";
import "./profile.css";
import { FaCheck, FaTimes, FaExclamationTriangle, FaCamera, FaChevronDown } from 'react-icons/fa';
import {
  Col,
  Container,
  Form,
  FormFeedback,
  Input,
  Label,
  Row,
  Card,
  CardBody,
  CardTitle,
  Button,
  Spinner,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem
} from "reactstrap";
import Header from "../../components/header";
import FacebookIntegration from "../../components/FacebookIntegration";
import { downloadChatMedia, getWhatsappProfile, updateProfile, uploadMedia } from "../../services/whatsapp";
import UltimatePlanInfo from "../../components/UltimatePlanInfo/UltimatePlanInfo";
import { Logger } from '../../utils/logger';

// Enhanced Interfaces with proper null handling
interface ErrorDetail {
  error_description?: string;
}

interface ApiResponse<T> {
  statusCode: number;
  status: boolean;
  data: T;
  message?: string;
}

interface Entity {
  entity_type: string;
  id: string;
  can_send_message: string;
  error_description?: string;
  errors?: ErrorDetail[];
  additional_info?: string[];
}

interface HealthStatus {
  can_send_message?: string;
  entities: Entity[];
}

interface QualityScore {
  score?: string;
  reason?: string;
  reasons?: string[];
}

interface UserProfileData {
  _id: string;
  phoneNumberId?: string;
  about?: string;
  accountMode?: string;
  address?: string;
  codeVerificationStatus?: string;
  description?: string;
  displayPhoneNumber?: string;
  eligibilityForApiBusinessGlobalSearch?: string;
  email?: string;
  healthStatus?: HealthStatus;
  isOfficialBusinessAccount?: boolean;
  isPinEnabled?: boolean;
  messagingLimitTier?: string;
  messagingProduct?: string;
  nameStatus?: string;
  profilePictureHandle?: string;
  profilePictureUrl?: string;
  qualityScore?: QualityScore;
  searchVisibility?: string;
  status?: string;
  throughput?: {
    level?: string;
  };
  verifiedName?: string;
  vertical?: string;
  websites?: string[];
  whatsappWabaAccount?: string;
  accountAnalytics?: {
    aggregation: {
      sent: number;
      delivered: number;
      startDate: string | null;
      endDate: string | null;
    };
    data: any[];
  };
}

interface WhatsappProfileResponse {
  message: string;
  statusCode: number;
  status: boolean;
  data: UserProfileData | UserProfileData[] | null;
}

const CATEGORY_OPTIONS = [
  'OTHER', 'AUTO', 'BEAUTY', 'APPAREL', 'EDU', 'ENTERTAIN',
  'EVENT_PLAN', 'FINANCE', 'GROCERY', 'GOVT', 'HOTEL', 'HEALTH', 'NONPROFIT',
  'PROF_SERVICES', 'RETAIL', 'TRAVEL', 'RESTAURANT', 'NOT_A_BIZ'
];

// Safe utility functions with null/undefined protection
const safeGetStatusColor = (status?: string | null): string => {
  if (!status || typeof status !== 'string') {
    console.warn('safeGetStatusColor received invalid status:', status);
    return 'text-secondary';
  }

  switch (status.toLowerCase()) {
    case 'available':
    case 'connected':
    case 'green':
      return 'text-success';
    case 'limited':
    case 'declined':
      return 'text-danger';
    case 'yellow':
    case 'warning':
      return 'text-warning';
    default:
      return 'text-warning';
  }
};

const safeGetStatusIcon = (status?: string | null): JSX.Element => {
  if (!status || typeof status !== 'string') {
    return <FaExclamationTriangle className="me-2" />;
  }

  switch (status.toLowerCase()) {
    case 'available':
    case 'connected':
    case 'green':
      return <FaCheck className="me-2" />;
    case 'limited':
    case 'declined':
      return <FaTimes className="me-2" />;
    default:
      return <FaExclamationTriangle className="me-2" />;
  }
};

// Safe entity filtering with null protection
const safeFilterEntities = (entities?: Entity[], entityType?: string): Entity[] => {
  if (!Array.isArray(entities) || !entityType) {
    return [];
  }
  return entities.filter((entity) => entity?.entity_type === entityType);
};

// Safe messaging status check
const safeIsMessagingBlocked = (entities?: Entity[]): boolean => {
  if (!Array.isArray(entities)) {
    return true; // Assume blocked if no entity data
  }

  const phoneEntity = entities.find(e => e?.entity_type === 'PHONE_NUMBER');
  const wabaEntity = entities.find(e => e?.entity_type === 'WABA');
  
  const phoneBlocked = phoneEntity?.can_send_message === 'LIMITED' || phoneEntity?.can_send_message === 'DECLINED';
  const wabaBlocked = wabaEntity?.can_send_message === 'LIMITED' || wabaEntity?.can_send_message === 'DECLINED';
  
  if (phoneBlocked && wabaEntity?.can_send_message === 'AVAILABLE') {
    return false;
  }
  
  return phoneBlocked || wabaBlocked;
};

// Safe messaging status info
const safeGetMessagingStatus = (entities?: Entity[]): { status: string; type: 'blocked' | 'limited' | 'available'; reasons: string[] } => {
  if (!Array.isArray(entities)) {
    return {
      status: 'Status Unknown',
      type: 'blocked',
      reasons: ['No health status data available']
    };
  }

  const phoneEntity = entities.find(e => e?.entity_type === 'PHONE_NUMBER');
  const wabaEntity = entities.find(e => e?.entity_type === 'WABA');
  
  const phoneBlocked = phoneEntity?.can_send_message === 'LIMITED' || phoneEntity?.can_send_message === 'DECLINED';
  const wabaBlocked = wabaEntity?.can_send_message === 'LIMITED' || wabaEntity?.can_send_message === 'DECLINED';
  
  if (wabaBlocked) {
    return {
      status: 'Messaging Completely Blocked',
      type: 'blocked',
      reasons: safeGetBlockingReasons(entities)
    };
  }
  
  if (phoneBlocked && wabaEntity?.can_send_message === 'AVAILABLE') {
    return {
      status: 'Messaging Limited',
      type: 'limited', 
      reasons: safeGetBlockingReasons([phoneEntity].filter(Boolean))
    };
  }
  
  return {
    status: 'Messaging Available',
    type: 'available',
    reasons: []
  };
};

// Safe message limit parsing
const safeParseMessageLimit = (tier?: string): string => {
  if (!tier || typeof tier !== 'string') {
    return 'Unknown message limit';
  }

  const tierMappings: { [key: string]: string } = {
    'TIER_10K': '10,000 messages per day',
    'TIER_1K': '1,000 messages per day', 
    'TIER_250': '250 messages per day',
    'TIER_50': '50 messages per day',
    'TIER_10': '10 messages per day'
  };
  
  return tierMappings[tier] || tier;
};

// Safe quality score info
const safeGetQualityScoreInfo = (score?: string): { severity: 'critical' | 'warning' | 'good'; message: string; color: string } => {
  if (!score || typeof score !== 'string') {
    return {
      severity: 'warning',
      message: 'Quality status unknown',
      color: 'var(--text-secondary)'
    };
  }

  switch (score.toUpperCase()) {
    case 'RED':
      return {
        severity: 'critical',
        message: 'CRITICAL - Account at risk of being banned',
        color: 'var(--error-red)'
      };
    case 'YELLOW':
      return {
        severity: 'warning', 
        message: 'WARNING - Quality needs improvement',
        color: 'var(--warning-orange)'
      };
    case 'GREEN':
      return {
        severity: 'good',
        message: 'GOOD - Quality rating healthy',
        color: 'var(--success-green)'
      };
    default:
      return {
        severity: 'warning',
        message: 'Quality status unknown',
        color: 'var(--text-secondary)'
      };
  }
};

// Safe blocking reasons extraction
const safeGetBlockingReasons = (entities?: Entity[]): string[] => {
  if (!Array.isArray(entities)) {
    return [];
  }

  const reasons: string[] = [];
  
  entities.forEach(entity => {
    if (!entity) return;
    
    if (entity.can_send_message === 'LIMITED' || entity.can_send_message === 'DECLINED') {
      if (entity.error_description) {
        reasons.push(entity.error_description);
      }
      
      if (Array.isArray(entity.additional_info)) {
        reasons.push(...entity.additional_info);
      }
      
      if (Array.isArray(entity.errors)) {
        entity.errors.forEach(error => {
          if (error?.error_description) {
            reasons.push(error.error_description);
          }
        });
      }
    }
  });
  
  return [...new Set(reasons)];
};

// Safe data validation helpers
const isValidProfile = (profile: any): profile is UserProfileData => {
  return profile && 
         typeof profile === 'object' && 
         typeof profile._id === 'string' && 
         profile._id.length > 0;
};

const hasCompleteHealthStatus = (profile: UserProfileData): boolean => {
  return !!(profile?.healthStatus?.entities && Array.isArray(profile.healthStatus.entities));
};

export default function Profile() {
  // State management with proper initialization
  const [showFbButton, setShowFbButton] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [allProfiles, setAllProfiles] = useState<UserProfileData[]>([]);
  const [selectedProfileId, setSelectedProfileId] = useState<string>('');
  const [userData, setUserData] = useState<UserProfileData>({} as UserProfileData);
  const [wabaEntities, setWabaEntities] = useState<Entity[]>([]);
  const [businessEntities, setBusinessEntities] = useState<Entity[]>([]);
  const [phoneNumberEntities, setPhoneNumberEntities] = useState<Entity[]>([]);
  const [appEntities, setAppEntities] = useState<Entity[]>([]);
  const [isCheckingIntegration, setIsCheckingIntegration] = useState(true);
  const [isIntegrated, setIsIntegrated] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [profileImage, setProfileImage] = useState<string | null>(null);
  const [isLoadingImage, setIsLoadingImage] = useState(false);
  const [isUltimate, setIsUltimate] = useState<boolean | null>(null);
  const [phoneDropdownOpen, setPhoneDropdownOpen] = useState(false);
  
  // New state for handling integration-but-no-data scenario
  const [hasNoProfileData, setHasNoProfileData] = useState(false);

  // Safe integration status check
  const checkIntegrationStatus = (): boolean => {
    try {
      Logger.log('Checking integration status - START');
      const userDataString = localStorage.getItem("userData");
      
      if (!userDataString) {
        Logger.log('No userData in localStorage');
        return false;
      }

      const userData = JSON.parse(userDataString);
      Logger.log('Parsed userData:', userData);

      const userIntegrations = userData?.userIntegrations;
      if (!Array.isArray(userIntegrations)) {
        Logger.log('No userIntegrations array found');
        return false;
      }

      Logger.log('User Integrations:', userIntegrations);

      const isIntegrated = userIntegrations.some((item) => {
        const integrationId = item?.integration?._id;
        const isActive = item?.isActive;
        const isMatch = integrationId === "646b9286ae8ef73b33caec2e" && isActive;
        
        Logger.log('Integration Check:', {
          integrationId,
          isActive,
          isMatch
        });
        
        return isMatch;
      });

      Logger.log('Final Integration Status:', isIntegrated);
      return isIntegrated;
    } catch (error) {
      console.error("Error checking integration status:", error);
      return false;
    }
  };

  // Safe profile picture loading
  useEffect(() => {
    const profilePictureHandle = userData?.profilePictureHandle;
    const profilePictureUrl = userData?.profilePictureUrl;
    
    if (profilePictureHandle && typeof profilePictureHandle === 'string') {
      loadProfilePicture(profilePictureHandle);
    } else if (profilePictureUrl && typeof profilePictureUrl === 'string') {
      console.warn('WhatsApp profile URL detected, using placeholder instead:', profilePictureUrl);
      setProfileImage(null);
    } else {
      setProfileImage(null);
    }
  }, [userData?.profilePictureHandle, userData?.profilePictureUrl]);

  // Safe ultimate plan check
  useEffect(() => {
    try {
      const localData = window.localStorage?.getItem("userData");
      if (localData) {
        const userData = JSON.parse(localData);
        const planType = userData?.subscriptionPlanType;
        setIsUltimate(typeof planType === 'string' && planType.toLowerCase() === "ultimate");
      }
    } catch (error) {
      console.error('Error checking ultimate plan:', error);
      setIsUltimate(false);
    }
  }, []);

  // Enhanced data fetching with proper error boundaries
  const fetchUserData = async (): Promise<void> => {
    Logger.log('Fetch User Data - START');
    
    try {
      const isIntegrated = checkIntegrationStatus();
      Logger.log('Integration Status in fetchUserData:', isIntegrated);
  
      if (!isIntegrated) {
        Logger.warn('Not integrated - stopping fetch');
        setIsLoading(false);
        setShowFbButton(true);
        setHasNoProfileData(false);
        return;
      }
  
      Logger.log('Proceeding with API call');
      setIsLoading(true);
      setError(null);
      setHasNoProfileData(false);
      
      const axiosResponse = await getWhatsappProfile();
      Logger.log('Axios Response:', axiosResponse);
      
      const response: WhatsappProfileResponse = axiosResponse?.data;
      Logger.log('API Response:', response);
  
      // Enhanced response validation
      if (!response) {
        throw new Error('No response received from API');
      }

      if (response.statusCode !== 200 || !response.status) {
        throw new Error(`API call failed with status: ${response.statusCode} - ${response.message || 'Unknown error'}`);
      }

      const responseData = response.data;
      Logger.log('Response Data:', responseData);
      
      // Handle null/undefined data case
      if (!responseData) {
        Logger.warn('API returned success but no data - user may need to complete WhatsApp setup');
        setHasNoProfileData(true);
        setIsLoading(false);
        return;
      }

      // Process profiles with validation
      let profiles: UserProfileData[] = [];
      
      if (Array.isArray(responseData)) {
        profiles = responseData.filter(isValidProfile);
        Logger.log('Multiple profiles detected, valid count:', profiles.length);
      } else if (isValidProfile(responseData)) {
        profiles = [responseData];
        Logger.log('Single valid profile detected');
      } else {
        Logger.warn('Invalid response format - data is not a valid profile object or array');
        setHasNoProfileData(true);
        setIsLoading(false);
        return;
      }
      
      if (profiles.length === 0) {
        Logger.warn('No valid profiles found in response');
        setHasNoProfileData(true);
        setIsLoading(false);
        return;
      }

      setAllProfiles(profiles);
      setHasNoProfileData(false);
      
      // Set target profile safely
      if (profiles.length > 0) {
        const targetProfile = selectedProfileId 
          ? profiles.find(p => p?._id === selectedProfileId) || profiles[0]
          : profiles[0];
          
        setSelectedProfileId(targetProfile._id);
        setCurrentProfile(targetProfile);
      }
      
    } catch (error: unknown) {
      console.error("Comprehensive Error in fetchUserData:", error);
      
      let errorMessage = "Failed to fetch profile data";
      
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }
      
      Logger.log('Error Details:', {
        errorMessage,
        type: typeof error,
        stringified: JSON.stringify(error, null, 2)
      });
  
      setError(errorMessage);
      setHasNoProfileData(false);
      toast.error(errorMessage);
    } finally {
      Logger.log('Fetch User Data - END');
      setIsLoading(false);
    }
  };

  // Safe profile setting with null protection
  const setCurrentProfile = (profile: UserProfileData): void => {
    if (!isValidProfile(profile)) {
      Logger.warn('Attempted to set invalid profile:', profile);
      return;
    }

    Logger.log('Setting current profile:', profile);
    setUserData(profile);
    
    // Safe entity processing
    const entities = profile?.healthStatus?.entities;
    if (Array.isArray(entities)) {
      Logger.log('Processing entities:', entities);

      const wabaEntities = safeFilterEntities(entities, "WABA");
      const businessEntities = safeFilterEntities(entities, "BUSINESS");
      const phoneNumberEntities = safeFilterEntities(entities, "PHONE_NUMBER");
      const appEntities = safeFilterEntities(entities, "APP");

      Logger.log('Filtered Entities:', {
        wabaEntities,
        businessEntities,
        phoneNumberEntities,
        appEntities
      });

      setWabaEntities(wabaEntities);
      setBusinessEntities(businessEntities);
      setPhoneNumberEntities(phoneNumberEntities);
      setAppEntities(appEntities);
    } else {
      // Clear entities if no valid health status
      setWabaEntities([]);
      setBusinessEntities([]);
      setPhoneNumberEntities([]);
      setAppEntities([]);
    }
  };

  // Safe phone profile switching
  const handlePhoneProfileChange = (profileId: string): void => {
    if (!profileId || typeof profileId !== 'string') {
      return;
    }

    const selectedProfile = allProfiles.find(profile => profile?._id === profileId);
    if (selectedProfile && isValidProfile(selectedProfile)) {
      setSelectedProfileId(profileId);
      setCurrentProfile(selectedProfile);
      setPhoneDropdownOpen(false);
      Logger.log('Switched to profile:', profileId);
    }
  };

  // Safe manual refresh
  const handleManualRefresh = async (): Promise<void> => {
    try {
      setIsCheckingIntegration(true);
      const status = checkIntegrationStatus();
      setIsIntegrated(status);
      setShowFbButton(!status);
      
      if (status) {
        await fetchUserData();
      }
    } catch (error) {
      console.error('Error in manual refresh:', error);
    } finally {
      setIsCheckingIntegration(false);
    }
  };

  // Safe initialization
  useEffect(() => {
    const initializeProfile = async (): Promise<void> => {
      Logger.log('Initializing Profile - START');
      
      try {
        const status = checkIntegrationStatus();
        Logger.log('Initial Integration Status:', status);
        
        setIsIntegrated(status);
        setShowFbButton(!status);
        
        if (status) {
          Logger.log('Calling fetchUserData');
          await fetchUserData();
        }
      } catch (error) {
        console.error('Initialization Error:', error);
      } finally {
        Logger.log('Initializing Profile - END');
        setIsCheckingIntegration(false);
      }
    };
  
    initializeProfile();
  }, []);

  // Safe form validation
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      vertical: userData?.vertical || "",
      websites: Array.isArray(userData?.websites) ? userData.websites : [""],
      email: userData?.email || "",
      address: userData?.address || "",
      description: userData?.description || "",
    },
    validationSchema: Yup.object({
      vertical: Yup.string().required("Category is required"),
      websites: Yup.array().of(Yup.string().url("Invalid URL")).min(1, "At least one website is required"),
      email: Yup.string().email("Invalid email").required("Email is required"),
      address: Yup.string().required("Address is required"),
      description: Yup.string().required("Description is required"),
    }),
    onSubmit: async (values) => {
      try {
        setIsSubmitting(true);
        await updateProfile(values);
        toast.success("Profile updated successfully");
        await fetchUserData();
      } catch (error) {
        console.error("Error updating profile:", error);
        toast.error("Failed to update profile");
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  // Safe image upload handling
  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const files = e.target?.files;
    if (!files || files.length === 0) {
      return;
    }

    const file = files[0];
    if (!file) {
      return;
    }

    try {
      setIsUploading(true);
      
      const formData = new FormData();
      formData.append('file', file);
      
      const mediaResponse = await uploadMedia(formData);
      
      if (mediaResponse?.data?.data?.[0]?.uploadedId) {
        await updateProfile({
          ...validation.values,
          profilePictureHandle: mediaResponse.data.data[0].uploadedId
        });
        toast.success("Profile picture updated successfully");
        await fetchUserData();
      } else {
        throw new Error('No upload ID received from server');
      }
  
    } catch (error) {
      console.error("Error updating profile picture:", error);
      toast.error(error instanceof Error ? error.message : "Failed to update profile picture");
    } finally {
      setIsUploading(false);
    }
  };

  // Safe profile picture loading
  const loadProfilePicture = async (mediaId: string): Promise<void> => {
    if (!mediaId || typeof mediaId !== 'string') {
      setProfileImage(null);
      return;
    }

    try {
      setIsLoadingImage(true);
      const response = await downloadChatMedia({ mediaId });
      
      if (response?.data) {
        const blob = new Blob([response.data], { type: 'image/jpeg' });
        const imageUrl = URL.createObjectURL(blob);
        setProfileImage(imageUrl);
      } else {
        setProfileImage(null);
      }
    } catch (error) {
      console.error("Error loading profile picture:", error);
      setProfileImage(null);
    } finally {
      setIsLoadingImage(false);
    }
  };

  // Safe data extraction for display
  const messagingStatusInfo = hasCompleteHealthStatus(userData) 
    ? safeGetMessagingStatus(userData.healthStatus!.entities) 
    : { status: 'Status Unknown', type: 'blocked' as const, reasons: ['No health status data available'] };
  
  const blockingReasons = messagingStatusInfo.reasons;
  const qualityInfo = userData?.qualityScore?.score ? safeGetQualityScoreInfo(userData.qualityScore.score) : null;
  
  // Early return for non-ultimate users
  if (isUltimate === false) {
    return (
      <div id="main" className="main">
        <Header />
        <UltimatePlanInfo />
      </div>
    );
  }
  
  // Early return for integration but no data case
  if (hasNoProfileData) {
    return (
      <div id="main" className="main">
        <Header />
        <Container fluid className="py-4">
          <div className="integration-no-data-container">
            <Card className="text-center p-4">
              <CardBody>
                <div className="integration-no-data-icon mb-3">
                  <FaExclamationTriangle size={60} color="#ffc107" />
                </div>
                <h4 className="mb-3">WhatsApp Business Setup Incomplete</h4>
                <p className="text-muted mb-4">
                  Your Facebook integration is active, but your WhatsApp Business Account needs to be set up completely.
                  Please ensure you have:
                </p>
                <ul className="text-left mb-4">
                  <li>Added a phone number to your WhatsApp Business Account</li>
                  <li>Completed phone number verification</li>
                  <li>Finished business verification process</li>
                  <li>Configured your WhatsApp Business API properly</li>
                </ul>
                <div className="d-flex justify-content-center gap-3">
                  <Button color="primary" onClick={handleManualRefresh}>
                    {isCheckingIntegration ? <Spinner size="sm" className="me-2" /> : null}
                    Check Again
                  </Button>
                  <Button color="outline-secondary" onClick={() => setShowFbButton(true)}>
                    Reconfigure Integration
                  </Button>
                </div>
              </CardBody>
            </Card>
          </div>
        </Container>
      </div>
    );
  }
  
  return (
    <div id="main" className="main">
      <Header />
      {showFbButton ? (
        <FacebookIntegration
          onSuccess={() => {
            setShowFbButton(false);
            fetchUserData();
          }}
        />
      ) : (
        <Container fluid className="py-4">
          {/* Header Section with Phone Selector */}
          <div className="profile-header-section">
            <div className="profile-header-content">
              <h4 className="profile-title">WhatsApp Business API Profile</h4>
              
              {/* Phone Number Selector - only show if multiple profiles */}
              {allProfiles.length > 1 && (
                <div className="phone-selector-container">
                  <Dropdown 
                    isOpen={phoneDropdownOpen} 
                    toggle={() => setPhoneDropdownOpen(!phoneDropdownOpen)}
                    className="phone-selector-dropdown"
                  >
                    <DropdownToggle 
                      caret={false}
                      className="phone-selector-toggle"
                    >
                      <div className="phone-selector-content">
                        <div className="phone-number-display">
                          {userData?.displayPhoneNumber || 'Unknown Phone'}
                        </div>
                        <div className={`phone-status-display ${messagingStatusInfo.type === 'available' ? 'status-available' : 'status-limited'}`}>
                          {messagingStatusInfo.type === 'available' ? 'Messaging Available' : 
                           messagingStatusInfo.type === 'blocked' ? 'Messaging Blocked' : 'Messaging Limited'}
                        </div>
                      </div>
                      <FaChevronDown className="dropdown-icon" />
                    </DropdownToggle>
                    <DropdownMenu className="phone-selector-menu">
                      {allProfiles.map((profile) => {
                        if (!isValidProfile(profile)) return null;
                        
                        const profileStatusInfo = hasCompleteHealthStatus(profile)
                          ? safeGetMessagingStatus(profile.healthStatus!.entities)
                          : { status: 'Unknown', type: 'blocked' as const, reasons: [] };
                          
                        return (
                          <DropdownItem
                            key={profile._id}
                            onClick={() => handlePhoneProfileChange(profile._id)}
                            className={`phone-option ${selectedProfileId === profile._id ? 'active' : ''}`}
                          >
                            <div className="phone-option-content">
                              <div className="phone-option-number">
                                {profile.displayPhoneNumber || 'Unknown Phone'}
                              </div>
                              <div className={`phone-option-status ${profileStatusInfo.type === 'available' ? 'status-available' : 'status-limited'}`}>
                                {profileStatusInfo.type === 'available' ? 'Available' : 
                                 profileStatusInfo.type === 'blocked' ? 'Blocked' : 'Limited'}
                              </div>
                            </div>
                          </DropdownItem>
                        );
                      })}
                    </DropdownMenu>
                  </Dropdown>
                </div>
              )}
            </div>
          </div>

          {isLoading ? (
            <div className="loading-container">
              <Spinner color="primary" size="lg" />
              <p className="loading-text">Loading profile data...</p>
            </div>
          ) : error ? (
            <div className="error-container">
              <div className="error-icon">
                <FaTimes size={40} />
              </div>
              <p className="error-message">{error}</p>
              <Button color="primary" onClick={handleManualRefresh} className="retry-button">
                Try Again
              </Button>
            </div>
          ) : (
            <>
              {/* Messaging Status Alert */}
              {messagingStatusInfo.type !== 'available' && (
                <div className={`messaging-status-alert ${messagingStatusInfo.type}`}>
                  <div className="status-alert-header">
                    <div className="status-alert-icon">
                      {messagingStatusInfo.type === 'blocked' ? <FaTimes /> : <FaExclamationTriangle />}
                    </div>
                    <div className="status-alert-title">
                      {messagingStatusInfo.status}
                    </div>
                  </div>
                  <p className="status-alert-description">
                    {messagingStatusInfo.type === 'blocked' 
                      ? 'Your account cannot send messages due to the issues listed below. Please resolve these issues to restore messaging capability.'
                      : 'Your messaging capability is limited. Address the issues below to improve your messaging limits.'
                    }
                  </p>
                  {blockingReasons.length > 0 && (
                    <div className="blocking-reasons">
                      <div className="blocking-reasons-title">Issues to resolve:</div>
                      <ul className="blocking-reasons-list">
                        {blockingReasons.map((reason, index) => (
                          <li key={index}>{reason}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* Quality Score Critical Alert */}
              {qualityInfo && qualityInfo.severity === 'critical' && (
                <div className="quality-critical-alert">
                  <div className="critical-alert-header">
                    <div className="critical-alert-icon">
                      <FaExclamationTriangle />
                    </div>
                    <div className="critical-alert-content">
                      <div className="critical-alert-title">
                        Quality Score: {userData?.qualityScore?.score || 'Unknown'}
                      </div>
                      <div className="critical-alert-message">
                        {qualityInfo.message}
                      </div>
                    </div>
                  </div>
                  {userData?.qualityScore?.reason && (
                    <div className="critical-alert-details">
                      <strong>Details:</strong> {userData.qualityScore.reason}
                    </div>
                  )}
                </div>
              )}

              {/* Health Status Cards */}
              <Row className="health-status-grid">
                {/* WABA Health */}
                <Col lg={3} md={6} className="mb-4">
                  <Card className="health-status-card">
                    <CardBody>
                      <CardTitle tag="h5" className="health-card-title">WABA Health</CardTitle>
                      {wabaEntities.length > 0 && wabaEntities[0] ? (
                        <div className="health-status-content">
                          <div className={`health-status-item ${wabaEntities[0].can_send_message?.toLowerCase() || 'unknown'}`}>
                            {safeGetStatusIcon(wabaEntities[0].can_send_message)}
                            <span className="health-label">Status:</span>
                            <span className={`health-value ${safeGetStatusColor(wabaEntities[0].can_send_message)}`}>
                              {wabaEntities[0].can_send_message || 'Unknown'}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="health-status-content">
                          <div className="health-status-item unknown">
                            {safeGetStatusIcon(undefined)}
                            <span className="health-label">Status:</span>
                            <span className="health-value text-secondary">No Data</span>
                          </div>
                        </div>
                      )}
                    </CardBody>
                  </Card>
                </Col>

                {/* Business Health */}
                <Col lg={3} md={6} className="mb-4">
                  <Card className="health-status-card">
                    <CardBody>
                      <CardTitle tag="h5" className="health-card-title">Business Health</CardTitle>
                      {businessEntities.length > 0 && businessEntities[0] ? (
                        <div className="health-status-content">
                          <div className={`health-status-item ${businessEntities[0].can_send_message?.toLowerCase() || 'unknown'}`}>
                            {safeGetStatusIcon(businessEntities[0].can_send_message)}
                            <span className="health-label">Status:</span>
                            <span className={`health-value ${safeGetStatusColor(businessEntities[0].can_send_message)}`}>
                              {businessEntities[0].can_send_message || 'Unknown'}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="health-status-content">
                          <div className="health-status-item unknown">
                            {safeGetStatusIcon(undefined)}
                            <span className="health-label">Status:</span>
                            <span className="health-value text-secondary">No Data</span>
                          </div>
                        </div>
                      )}
                    </CardBody>
                  </Card>
                </Col>

                {/* App Health */}
                <Col lg={3} md={6} className="mb-4">
                  <Card className="health-status-card">
                    <CardBody>
                      <CardTitle tag="h5" className="health-card-title">App Health</CardTitle>
                      {appEntities.length > 0 && appEntities[0] ? (
                        <div className="health-status-content">
                          <div className={`health-status-item ${appEntities[0].can_send_message?.toLowerCase() || 'unknown'}`}>
                            {safeGetStatusIcon(appEntities[0].can_send_message)}
                            <span className="health-label">Status:</span>
                            <span className={`health-value ${safeGetStatusColor(appEntities[0].can_send_message)}`}>
                              {appEntities[0].can_send_message || 'Unknown'}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="health-status-content">
                          <div className="health-status-item unknown">
                            {safeGetStatusIcon(undefined)}
                            <span className="health-label">Status:</span>
                            <span className="health-value text-secondary">No Data</span>
                          </div>
                        </div>
                      )}
                    </CardBody>
                  </Card>
                </Col>

                {/* Phone Health */}
                <Col lg={3} md={6} className="mb-4">
                  <Card className="health-status-card">
                    <CardBody>
                      <CardTitle tag="h5" className="health-card-title">Phone Health</CardTitle>
                      <div className="health-status-content">
                        {phoneNumberEntities.length > 0 && phoneNumberEntities[0] ? (
                          <div className={`health-status-item ${phoneNumberEntities[0].can_send_message?.toLowerCase() || 'unknown'}`}>
                            {safeGetStatusIcon(phoneNumberEntities[0].can_send_message)}
                            <span className="health-label">Status:</span>
                            <span className={`health-value ${safeGetStatusColor(phoneNumberEntities[0].can_send_message)}`}>
                              {phoneNumberEntities[0].can_send_message || 'Unknown'}
                            </span>
                          </div>
                        ) : (
                          <div className="health-status-item unknown">
                            {safeGetStatusIcon(undefined)}
                            <span className="health-label">Status:</span>
                            <span className="health-value text-secondary">No Data</span>
                          </div>
                        )}
                        
                        <div className="health-status-item">
                          <span className="health-label">Phone:</span>
                          <span className="health-value">{userData?.displayPhoneNumber || 'Unknown'}</span>
                        </div>
                        
                        <div className="health-status-item">
                          <span className="health-label">Verified Name:</span>
                          <span className="health-value">{userData?.verifiedName || 'Unknown'}</span>
                        </div>
                      </div>

                      {/* Phone Number Error Details */}
                      {phoneNumberEntities.length > 0 && 
                       phoneNumberEntities[0] &&
                       (phoneNumberEntities[0].can_send_message === 'LIMITED' || 
                        phoneNumberEntities[0].can_send_message === 'DECLINED') && 
                       Array.isArray(phoneNumberEntities[0].additional_info) && 
                       phoneNumberEntities[0].additional_info!.length > 0 && (
                        <div className="error-details-section">
                          <div className="error-details-title">Issues:</div>
                          <ul className="error-details-list">
                            {phoneNumberEntities[0].additional_info!.map((info, index) => (
                              <li key={index}>{info}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </CardBody>
                  </Card>
                </Col>
              </Row>

              {/* Account Details Card */}
              <Row className="mb-4">
                <Col>
                  <Card className="account-details-card">
                    <CardBody>
                      <CardTitle tag="h5" className="account-card-title">Account Details</CardTitle>
                      <Row>
                        <Col md={3} className="mb-3">
                          <div className="account-detail-item">
                            <span className="account-label">Message Limit:</span>
                            <span className="account-value">{safeParseMessageLimit(userData?.messagingLimitTier)}</span>
                          </div>
                        </Col>
                        
                        <Col md={3} className="mb-3">
                          <div className={`account-detail-item ${userData?.nameStatus?.toLowerCase() || 'unknown'}`}>
                            {safeGetStatusIcon(userData?.nameStatus)}
                            <span className="account-label">Name Status:</span>
                            <span className={`account-value ${safeGetStatusColor(userData?.nameStatus)}`}>
                              {userData?.nameStatus || 'Unknown'}
                            </span>
                          </div>
                        </Col>
                        
                        <Col md={3} className="mb-3">
                          <div className={`account-detail-item quality-score-${userData?.qualityScore?.score?.toLowerCase() || 'unknown'}`}>
                            {safeGetStatusIcon(userData?.qualityScore?.score)}
                            <span className="account-label">Quality Score:</span>
                            <span className="account-value" style={{ color: qualityInfo?.color || 'var(--text-secondary)' }}>
                              {userData?.qualityScore?.score || 'Unknown'}
                            </span>
                          </div>
                        </Col>
                        
                        <Col md={3} className="mb-3">
                          <div className={`account-detail-item ${userData?.status?.toLowerCase() || 'unknown'}`}>
                            {safeGetStatusIcon(userData?.status)}
                            <span className="account-label">Connection:</span>
                            <span className={`account-value ${safeGetStatusColor(userData?.status)}`}>
                              {userData?.status || 'Unknown'}
                            </span>
                          </div>
                        </Col>
                      </Row>

                      {/* Quality Score Warning */}
                      {userData?.qualityScore?.score === 'YELLOW' && userData?.qualityScore?.reason && (
                        <div className="warning-details-section">
                          <div className="warning-details-title">Quality Score Warning:</div>
                          <p className="warning-details-text">{userData.qualityScore.reason}</p>
                        </div>
                      )}
                    </CardBody>
                  </Card>
                </Col>
              </Row>

              {/* Profile Form */}
              <Card className="profile-form-card">
                <CardBody>
                  <h5 className="profile-form-title">WhatsApp Cloud API Profile</h5>
                  <Row>
                    <Col md={4} className="profile-picture-section">
                      <div className="profile-picture-container">
                        {isLoadingImage ? (
                          <div className="profile-picture-loading">
                            <Spinner size="sm" />
                          </div>
                        ) : profileImage ? (
                          <img
                            className="profile-picture-image"
                            src={profileImage}
                            alt="Profile"
                            onError={(e) => {
                              console.warn('Profile image failed to load, showing placeholder');
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                              setProfileImage(null);
                            }}
                          />
                        ) : (
                          <div className="profile-picture-placeholder">
                            No Image
                          </div>
                        )}
                        
                        <label className="profile-picture-upload-btn" htmlFor="profile-picture">
                          {isUploading ? <Spinner size="sm" /> : <FaCamera />}
                        </label>
                        <input
                          type="file"
                          id="profile-picture"
                          onChange={handleImageChange}
                          accept="image/*"
                          hidden
                          disabled={isUploading}
                        />
                      </div>
                      <div className="profile-info-section">
                        <h4 className="profile-name">{userData?.verifiedName || 'Unknown Business'}</h4>
                        <p className="profile-description">{userData?.description || 'No description available'}</p>
                      </div>
                    </Col>

                    <Col md={8}>
                      <Form onSubmit={validation.handleSubmit}>
                        <Row>
                          <Col md={6} className="mb-3">
                            <Label className="form-label">Category</Label>
                            <Input
                              type="select"
                              name="vertical"
                              value={validation.values.vertical}
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              invalid={!!(validation.touched.vertical && validation.errors.vertical)}
                              className="form-input"
                            >
                              {CATEGORY_OPTIONS.map((option) => (
                                <option key={option} value={option}>
                                  {option}
                                </option>
                              ))}
                            </Input>
                            <FormFeedback>{validation.errors.vertical}</FormFeedback>
                          </Col>

                          <Col md={6} className="mb-3">
                            <Label className="form-label">Email</Label>
                            <Input
                              type="email"
                              name="email"
                              placeholder="Enter email"
                              value={validation.values.email}
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              invalid={!!(validation.touched.email && validation.errors.email)}
                              className="form-input"
                            />
                            <FormFeedback>{validation.errors.email}</FormFeedback>
                          </Col>

                          <Col md={6} className="mb-3">
                            <Label className="form-label">Website</Label>
                            <Input
                              type="url"
                              name="websites[0]"
                              placeholder="Enter website URL"
                              value={validation.values.websites[0] || ""}
                              onChange={(e) => {
                                validation.setFieldValue("websites", [e.target.value]);
                              }}
                              onBlur={validation.handleBlur}
                              invalid={!!(validation.touched.websites && validation.errors.websites)}
                              className="form-input"
                            />
                            <FormFeedback>
                              {typeof validation.errors.websites === 'string' 
                                ? validation.errors.websites 
                                : Array.isArray(validation.errors.websites) 
                                  ? validation.errors.websites[0] 
                                  : null}
                            </FormFeedback>
                          </Col>

                          <Col md={6} className="mb-3">
                            <Label className="form-label">Address</Label>
                            <Input
                              type="text"
                              name="address"
                              placeholder="Enter business address"
                              value={validation.values.address}
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              invalid={!!(validation.touched.address && validation.errors.address)}
                              className="form-input"
                            />
                            <FormFeedback>{validation.errors.address}</FormFeedback>
                          </Col>

                          <Col md={12} className="mb-3">
                            <Label className="form-label">Description</Label>
                            <Input
                              type="textarea"
                              name="description"
                              placeholder="Enter business description"
                              rows={4}
                              value={validation.values.description}
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              invalid={!!(validation.touched.description && validation.errors.description)}
                              className="form-input"
                            />
                            <FormFeedback>{validation.errors.description}</FormFeedback>
                          </Col>
                        </Row>

                        <Row>
                          <Col className="text-end mt-3">
                            <Button
                              type="submit"
                              color="primary"
                              disabled={isSubmitting}
                              className="save-changes-btn"
                            >
                              {isSubmitting ? (
                                <>
                                  <Spinner size="sm" className="me-2" />
                                  Saving...
                                </>
                              ) : (
                                "Save Changes"
                              )}
                            </Button>
                          </Col>
                        </Row>
                      </Form>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </>
          )}
        </Container>
      )}
      <Toaster 
        position="top-right" 
        reverseOrder={false}
        toastOptions={{
          duration: 4000,
          style: {
            background: '#fff',
            color: '#1a1a1a',
            border: '1px solid #e2e8f0',
            borderRadius: '12px',
          },
          success: {
            style: {
              border: '1px solid #16a34a',
            },
          },
          error: {
            style: {
              border: '1px solid #dc2626',
            },
          },
        }}
      />
    </div>
  );
}